<template>
    <Portal to="modal">
        <ModalContainer
            key="conceptQuizFeedbackSurveyModal"
            :is-dark-mode="isDarkMode"
            :show-close-button="true"
            @close="emitClose"
        >
            <div class="concept-quiz-feedback-survey">
                <div class="concept-quiz-feedback-survey__title-section" v-dark="isDarkMode">
                    <IconTarget
                        class="concept-quiz-feedback-survey__title-icon"
                    />
                    <div class="concept-quiz-feedback-survey__title">
                        How was your quiz on {{ conceptQuizVariant?.concept }}?
                    </div>
                    <div class="concept-quiz-feedback-survey__text" v-dark>
                        We would love your feedback.
                    </div>
                </div>
                <div class="concept-quiz-feedback-survey__form">
                    <Radio
                        v-model="improvedUnderstanding"
                        :data="yesNoOptions"
                        :is-dark-mode="isDarkMode"
                        class="concept-quiz-feedback-survey__field"
                        :label="`Did this quiz improve your understanding of ${conceptQuizVariant?.concept}?`"
                    />
                    <PocketTextarea
                        v-model="whyOrWhyNot"
                        label="Why or why not?"
                        class="concept-quiz-feedback-survey__field concept-quiz-feedback-survey__field-input"
                        :is-dark-mode="isDarkMode"
                        height="62"
                        width=""
                        @keydown.enter="submitForm"
                    />
                </div>
                <div class="concept-quiz-feedback-survey__actions">
                    <PocketButton
                        class="concept-quiz-feedback-survey__dismiss-btn"
                        :is-dark-mode="isDarkMode"
                        @click="emitClose"
                        type="secondary"
                    >
                        Cancel
                    </PocketButton>
                    <PocketButton
                        class="concept-quiz-feedback-survey__submit-btn"
                        :disabled="isSubmitDisabled"
                        :is-dark-mode="isDarkMode"
                        @click="submitForm"
                    >
                        Submit
                    </PocketButton>
                </div>
            </div>
        </ModalContainer>
    </Portal>
</template>

<script lang="ts">
import { Component, Emit, Vue } from 'vue-facing-decorator'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import { analyticsModule } from '@/store/analytics/module'
import { toastModule } from '@/store/toast/module'
import IconTarget from '@/components/ConceptQuizExperiment/IconTarget.vue'
import { examMetadataModule } from '@/store/examMetadata/module'
import { quizModule } from '@/store/quiz/module'

@Component({
    components: {
        ModalContainer: UIKit.ModalContainer,
        PocketButton: UIKit.Button,
        Modal: UIKit.Modal,
        PocketTextarea: UIKit.Textarea,
        Radio: UIKit.Radio,
        IconTarget,
    },
})
export default class ExplainYourAnswerFeedbackSurvey extends Vue {
    feedback = ''
    yesNoOptions = [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'unsure', label: 'I\'m not sure' },
    ]
    whyOrWhyNot = ''
    improvedUnderstanding: typeof this['yesNoOptions'][number] | null = null
    conceptQuizConfig: {
        concept: string
        examMetadataId: string
        conceptQuizSerials: string[]
        questionSerialTriggers?: string
    }[] = []

    get isDarkMode () {
        return userModule.state.settings.isDarkMode
    }

    get isSubmitDisabled () {
        return !this.improvedUnderstanding
    }

    get quizId () {
        return localStorage.getItem('conceptQuizExperiment_quizIdThatTriggeredConceptQuiz')
    }

    get triggeringQuiz () {
        return this.quizId
            ? quizModule.getters.getQuizById(this.quizId)
            : undefined
    }

    get triggeringQuizAnswers () {
        if (!this.triggeringQuiz) {
            return []
        }

        return this.triggeringQuiz.answers
    }

    get examMetadata () {
        return examMetadataModule.getters.getCurrentExamMetadata()
    }

    get triggerKeywordInfo () {
        const triggerKeywordInfoRaw = localStorage.getItem('conceptQuizExperiment_triggerKeywordInfo')
        const triggerKeywordInfo = triggerKeywordInfoRaw && JSON.parse(triggerKeywordInfoRaw) as {
            startedAt: string
            serial: string
            keyword: string
        }
        return triggerKeywordInfo || undefined
    }

    get keywordTriggeredConceptQuiz () {
        const userClickedKeywordInCurrentQuiz = this.triggerKeywordInfo 
            && new Date(this.triggerKeywordInfo.startedAt).toISOString() === this.triggeringQuiz?.startedAt.iso
        if (!this.triggerKeywordInfo || !userClickedKeywordInCurrentQuiz) {
            return undefined
        }

        return this.triggerKeywordInfo.serial
    }

    get questionTriggeredConceptQuiz () {
        if (!this.conceptQuizConfig) {
            return undefined
        }

        const questionSerialTriggers = this.conceptQuizConfig.flatMap(c => c.questionSerialTriggers)
        const conceptQuizQuestionAnswerTriggerSerial = this.triggeringQuizAnswers.find(
            a => questionSerialTriggers.includes(a.questionSerial) && !a.isCorrect
        )?.questionSerial

        return conceptQuizQuestionAnswerTriggerSerial
    }

    get conceptQuizVariant () {
        if (this.keywordTriggeredConceptQuiz) {
            return this.conceptQuizConfig.find(c => c.concept === this.triggerKeywordInfo?.keyword)
        } else if (this.questionTriggeredConceptQuiz) {
            return this.conceptQuizConfig.find(c =>
                this.questionTriggeredConceptQuiz
                && c.questionSerialTriggers?.includes(this.questionTriggeredConceptQuiz)
            )
        }

        return undefined
    }

    async mounted () {
        const variant = await analyticsModule.actions.amplitudeFetchVariant('concept-quiz')
        this.conceptQuizConfig = variant?.payload || null

        if (!this.conceptQuizVariant) {
            this.emitClose()
        }
    }

    submitForm () {
        analyticsModule.actions.amplitudeTrack('Concept Quiz Survey Answered', {
            conceptQuizSurveyWhyOrWhyNot: this.whyOrWhyNot,
            conceptQuizSurveyImprovedUnderstanding: this.improvedUnderstanding?.value,
            examGuid: this.examMetadata?.examGuid || '',
            conceptQuizConcept: this.conceptQuizVariant?.concept,
            conceptQuizTriggerKeywordClicked: !!this.keywordTriggeredConceptQuiz,
            conceptQuizTriggerQuestionAnswered: !!this.questionTriggeredConceptQuiz,
        })
        localStorage.setItem('conceptQuizExperiment_hasAnsweredSurvey', 'true')
        toastModule.actions.displayToast({
            title: 'Thank you for your feedback!',
        })
        this.emitClose()
    }

    @Emit('close')
    emitClose () {
        return true
    }
}
</script>

<style lang="scss" scoped>
.concept-quiz-feedback-survey {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 520px;
    box-sizing: border-box;
    
    @include breakpoint(black-bear) {
        width: 100%;
        margin: 0 auto;
    }

    &__title-section {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 24px 32px 0;
        box-sizing: border-box;
        border-bottom: 1px solid $fog;

        @include breakpoint(black-bear) {
            padding: 24px 32px 0 32px;
        }

        &--dark {
            border-color: $ash;
        }
    }

    &__title-icon {
        width: 18px;
        height: 18px;
        margin-bottom: 8px;
        color: $ash;
    }

    &__title {
        font-weight: 600;
        font-size: 18px;
        line-height: 25px;
        margin-bottom: 6px;
        letter-spacing: 0.3px;

        &--explanation {
            margin-bottom: 30px;
        }

        @include breakpoint(black-bear) {
            width: 110%;
            text-align: center;
        }
    }

    &__text {
        font-size: 15px;
        line-height: 20px;
        color: $ash;
        margin-bottom: 30px;

        &--dark {
            color: $fog;
        }
    }

    &__form {
        margin-top: 30px;
        padding: 0 40px;
        box-sizing: border-box;
        width: 100%;
    }

    &__modal {
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 94px;
    }

    &__field {
        margin-bottom: 20px;
    }

    &__field :deep(.uikit-radio__label),
    &__field :deep(.uikit-textarea__label) {
        color: $brand-black;
        margin-left: 0;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;

        @include breakpoint(black-bear) {
            padding-right: 0;
        }

        &.uikit-radio__label--dark,
        &.uikit-textarea__label--dark {
            color: $white;
        }
    }

    &__field :deep(.uikit-radio__label) {
        margin-bottom: 10px;
    }

    &__field :deep(.uikit-radio__item--dark::before) {
        background-color: $pickled-bluewood;
    }

    &__field-input :deep(.uikit-textarea__textarea) {
        margin-left: -8px;
        margin-right: -8px;
        width: calc(100% + 16px);
        height: 62px;
        display: block;

        @include breakpoint(black-bear) {
            height: 72px;
        }
    }

    &__actions {
        margin-top: 10px;
        align-self: center;
        padding: 0 40px 32px;
    }

    &__submit-btn {
        margin-left: 8px;
    }
}
</style>